import {skipToken, useQuery, useQueryClient, UseQueryOptions} from '@tanstack/react-query';
import {type} from 'arktype';
import {CountryCode, MethodId, MethodType} from '~/entities/operations-fiat';
import {expectEither, HttpClient, useHttpClient} from '~/library/network-facade';
import {AccountId} from '~/library/types';

const Requisite = type({
    key: 'string',
    label: 'string',
    value: 'string',
});

const BankWithRequisites = type({
    id: 'number',
    name: 'string',
    description: 'string | null',
    country: CountryCode.or('null'),
    icon: 'string.url | null',
    requisite: Requisite.array(),
});

const RequisitesData = BankWithRequisites.array();
type RequisitesData = typeof RequisitesData.infer;

async function fetchTopupPaymentNetworkFee(http: HttpClient, accountId: AccountId, methodId?: MethodId) {
    return http(`v1/accounts/${accountId}/receive/banks/${methodId}`).then(
        ...expectEither({
            200: RequisitesData,
        })
    );
}

export type UseTopupRequisitesOptionsData = {
    accountId: AccountId;
    methodType: MethodType | undefined;
    methodId: MethodId | undefined;
};

export function useTopupRequisitesOptions({
    accountId,
    methodType,
    methodId,
}: UseTopupRequisitesOptionsData): UseQueryOptions<RequisitesData> {
    const http = useHttpClient();

    return {
        queryKey: ['v1/accounts/{accountId}/receive/banks/{paymentNetworkId}', accountId, methodType, methodId],
        queryFn:
            methodType !== 'payment_network' || !methodId
                ? skipToken
                : async ({queryKey, client}) => {
                      const res = await fetchTopupPaymentNetworkFee(http, accountId, methodId);

                      switch (res.status) {
                          case 200:
                              return res.json;
                          case 'Abort': {
                              await client.cancelQueries({queryKey});
                              throw res.reason;
                          }
                      }
                  },
        enabled: methodType === 'payment_network' && !!methodId,
    };
}

export function useTopupRequisites(data: UseTopupRequisitesOptionsData) {
    const options = useTopupRequisitesOptions(data);
    return useQuery(options);
}

export function useTopupRequisitesPrefetch(data: UseTopupRequisitesOptionsData) {
    const options = useTopupRequisitesOptions(data);
    const client = useQueryClient();

    if (!client.getQueryState(options.queryKey) && options.enabled) {
        client.prefetchQuery(options);
    }
}
